import { Fragment, useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { getEmpresa, getEmpresas, getDfn, saveProyeccionConfig } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Scale, Pencil, Plus, Trash2, ChevronRight, ChevronDown, ArrowUpRight, Info } from "lucide-react";
import { cn, formatEur } from "@/lib/utils";

// DFN — Deuda Financiera Neta (PLAN_DFN.md, fase 1). Réplica de la pestaña
// «DFN» del modelo de puesta en venta: la DFN es lo que se resta del
// Enterprise Value para llegar al Equity Value. Es una LECTURA del modelo:
// histórico por las partidas del balance, proyectado por las series del
// consolidado (misma fuente que Resumen y las Comparativas) y «otras deudas
// asimiladas» como hipótesis manual que NO toca el modelo. Gris = real ·
// amarillo "Proy." = proyectado. "N/D" solo cuando de verdad no se puede saber.

const fmt = (v) => (v == null || Number.isNaN(Number(v)) ? "N/D" : formatEur(v));
const fmtX = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "N/D"
    : `${new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)}x`;

// Semáforo del múltiplo: misma escala que el Dashboard (<3x cómodo · 3-4x
// vigilar · >4x apalancamiento elevado). Los umbrales de transacción del PdV
// (3,5x comprador financiero / 4,5x refinanciación bancaria) van en la ayuda.
const colorRatio = (v) => {
  if (v == null) return "";
  if (v < 0) return "text-emerald-600";
  if (v <= 3) return "text-emerald-600";
  if (v <= 4) return "text-amber-600";
  return "text-rose-600";
};

const FILAS = [
  { k: "deuda_lp", l: "Deudas a largo plazo", hint: "Balance: II. Deudas a largo plazo + III. Deudas con empresas del grupo l/p (histórico) · serie «Deuda financiera LP» del consolidado (proyectado)" },
  { k: "deuda_cp", l: "Deudas a corto plazo", hint: "Balance: II. Deudas a corto plazo + III. Deudas con empresas del grupo c/p (histórico) · serie «Deuda financiera CP» del consolidado (proyectado)" },
  { k: "asimiladas", l: "(+) Otras deudas asimiladas", hint: "Hipótesis manual por año: préstamos de socios fuera de 17x, aplazamientos AEAT/TGSS, factoring con recurso, earn-outs, rentings reexpresados… No toca el modelo.", editable: true },
  { k: "deuda_bruta", l: "= Deuda financiera bruta", strong: true, hint: "Deudas LP + Deudas CP + otras deudas asimiladas" },
  { k: "tesoreria", l: "(−) Tesorería y equivalentes", hint: "Balance: VI. Efectivo, solo saldos deudores (un 57x acreedor es un descubierto: va a Deudas c/p, regla F2) · serie «Tesorería» del consolidado", neg: true },
  { k: "ifcp", l: "(−) Inversiones financieras c/p", hint: "Balance: IV. Inversiones financieras a corto plazo · proyectado: plana al año base mientras «Otros activos» sea plano", neg: true, switchIfcp: true },
  { k: "dfn", l: "= DFN contable", strong: true, dfn: true, hint: "Deuda bruta − tesorería − inversiones financieras c/p. Negativa = caja neta." },
  { sep: "MÚLTIPLOS" },
  { k: "ebitda", l: "EBITDA reportado", hint: "PyG del ejercicio (histórico) · kpis.ebitda del consolidado (proyectado)" },
  { k: "dfn_ebitda", l: "DFN / EBITDA reportado", ratio: true, hint: "DFN ÷ EBITDA reportado. N/D si el EBITDA es ≤ 0." },
  { k: "ebitda_ajustado", l: "EBITDA ajustado / normalizado", hint: "EBITDA ajustado del ejercicio (histórico) · EBITDA normalizado del consolidado si hay ajustes, si no el EBITDA (proyectado)" },
  { k: "dfn_ebitda_ajustado", l: "DFN / EBITDA ajustado", ratio: true, strong: true, hint: "El múltiplo que se enseña en una transacción. Orientativo: por encima de 3,5x un comprador financiero se retira o baja el precio; por encima de 4,5x un banco no refinancia." },
];

function AsimiladasDialog({ open, onOpenChange, anios, esProy, inicial, onGuardar }) {
  const [lineas, setLineas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => {
    if (open) {
      setLineas((inicial || []).map((a) => ({ concepto: a.concepto || "", por_anio: { ...(a.por_anio || {}) } })));
    }
  }, [open, inicial]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const limpias = lineas
        .filter((l) => l.concepto.trim())
        .map((l) => ({
          concepto: l.concepto.trim(),
          por_anio: Object.fromEntries(
            anios
              .map((a) => [String(a), l.por_anio?.[String(a)]])
              .filter(([, v]) => v !== "" && v != null && !Number.isNaN(parseFloat(String(v).replace(",", "."))))
              .map(([a, v]) => [a, parseFloat(String(v).replace(",", "."))])
          ),
        }));
      // onGuardar devuelve false si el backend rechazó el guardado (el toast
      // ya lo dice): el diálogo se queda abierto con lo tecleado.
      if (await onGuardar(limpias)) onOpenChange(false);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Otras deudas asimiladas a financiera</DialogTitle>
          <DialogDescription>
            Importe del saldo a cierre de cada año (positivo = deuda). Histórico: un año vacío
            cuenta 0. Proyección: un año vacío hereda el último importe definido. Ejemplos:
            préstamos de socios fuera de 17x, aplazamientos AEAT/TGSS, factoring con recurso,
            earn-outs pendientes, rentings reexpresados como deuda. No toca el balance ni el
            modelo: solo la DFN.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: `${280 + anios.length * 120}px` }}>
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="text-left py-1.5 pr-3 font-medium w-[280px]">Concepto</th>
                {anios.map((a) => (
                  <th key={a} className={cn("text-right py-1.5 px-2 font-mono font-medium whitespace-nowrap", esProy(a) && "text-proy")}>
                    {a}
                    <span className={cn("ml-1 text-[10px] font-semibold uppercase rounded px-1 py-0.5 align-middle", esProy(a) ? "bg-proy-strong text-proy" : "bg-muted")}>
                      {esProy(a) ? "Proy." : "Hist."}
                    </span>
                  </th>
                ))}
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => (
                <tr key={i} className="border-b">
                  <td className="py-1 pr-3">
                    <Input
                      value={l.concepto}
                      placeholder="Concepto (p.ej. Aplazamiento AEAT)"
                      className="h-7 text-xs"
                      onChange={(e) => {
                        const v = e.target.value;
                        setLineas((s) => s.map((x, j) => (j === i ? { ...x, concepto: v } : x)));
                      }}
                      data-testid={`dfn-asim-concepto-${i}`}
                    />
                  </td>
                  {anios.map((a) => (
                    <td key={a} className="text-right py-1 px-2">
                      <Input
                        type="number"
                        step="any"
                        placeholder={esProy(a) ? "hereda" : "0"}
                        className="h-7 text-right font-mono text-xs"
                        value={l.por_anio?.[String(a)] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLineas((s) => s.map((x, j) =>
                            j === i ? { ...x, por_anio: { ...x.por_anio, [String(a)]: v } } : x));
                        }}
                        data-testid={`dfn-asim-importe-${i}-${a}`}
                      />
                    </td>
                  ))}
                  <td className="text-center">
                    <button type="button" className="text-muted-foreground hover:text-destructive" title="Quitar línea"
                      onClick={() => setLineas((s) => s.filter((_, j) => j !== i))} data-testid={`dfn-asim-borrar-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {lineas.length === 0 && (
                <tr><td colSpan={anios.length + 2} className="py-3 text-xs text-muted-foreground text-center">Sin líneas. Añade una para empezar.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" size="sm" className="h-8 gap-1 text-xs"
            onClick={() => setLineas((s) => [...s, { concepto: "", por_anio: {} }])} data-testid="dfn-asim-anadir">
            <Plus className="h-3.5 w-3.5" /> Añadir línea
          </Button>
          <Button size="sm" className="h-8 text-xs" onClick={guardar} disabled={guardando} data-testid="dfn-asim-guardar">
            {guardando ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DfnPage() {
  const { empresaId } = useParams();
  const [empresa, setEmpresa] = useState(null);
  const [dfn, setDfn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogo, setDialogo] = useState(false);
  const [asimAbiertas, setAsimAbiertas] = useState(true);
  const [umbral, setUmbral] = useState("");
  const [gruposAbiertos, setGruposAbiertos] = useState({});
  const [myRole, setMyRole] = useState("owner");
  const readOnly = myRole === "read";

  const cargar = useCallback(async () => {
    try {
      const [emp, res, todas] = await Promise.all([getEmpresa(empresaId), getDfn(empresaId), getEmpresas()]);
      setEmpresa(emp.data);
      setDfn(res.data);
      // Rol en esta empresa (lista unificada, incluye compartidas): en solo
      // lectura los controles se desactivan en vez de devolver un 403.
      setMyRole(((todas.data || []).find((e) => e.id === empresaId) || {}).my_role || "owner");
      setUmbral(String(res.data?.config?.umbral_conciliacion ?? 50000));
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al cargar la DFN");
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    setLoading(true);
    cargar();
  }, [cargar]);

  // Devuelve true/false; nunca relanza (antes el error quedaba sin capturar
  // en el switch y los botones: "Uncaught (in promise)" y overlay de CRA).
  const guardarConfig = async (parcial, mensaje) => {
    try {
      await saveProyeccionConfig(empresaId, { dfn: parcial });
      toast.success(mensaje);
      await cargar();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
      return false;
    }
  };

  const guardarUmbral = () => {
    const v = parseFloat(String(umbral).replace(",", "."));
    if (!Number.isFinite(v) || v < 0) {
      // Campo vacío o no numérico: antes se guardaba 0 y la conciliación
      // salía "REVISAR" para siempre.
      toast.error("El umbral tiene que ser un número ≥ 0");
      return;
    }
    guardarConfig({ umbral_conciliacion: v }, "Umbral de conciliación guardado");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-4 animate-fade-in">
        <h1 className="page-title flex items-center gap-2"><Scale className="h-6 w-6 text-primary" /> Deuda Financiera Neta</h1>
        <Card><CardContent className="py-6 text-sm text-muted-foreground">{error}</CardContent></Card>
      </div>
    );
  }

  const anios = dfn.anios || [];
  const aniosProy = dfn.anios_proyectados || [];
  const esProy = (a) => aniosProy.includes(a);
  const filas = dfn.filas || {};
  const cfg = dfn.config || {};
  const conc = dfn.conciliacion;
  const base = dfn.anio_base;
  const v = (k, a) => filas[k]?.[String(a)];

  const celda = (fila, a) => {
    const val = v(fila.k, a);
    if (fila.ratio) return <span className={cn("font-semibold", colorRatio(val))}>{fmtX(val)}</span>;
    if (fila.dfn && val != null) return <span className={val > 0 ? "text-rose-600" : "text-emerald-600"}>{fmt(val)}</span>;
    if (fila.neg && val != null) return `(${fmt(val)})`;
    return fmt(val);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dfn-page">
      <div>
        <h1 className="page-title flex items-center gap-2">
          <Scale className="h-6 w-6 text-primary" /> Deuda Financiera Neta (DFN)
        </h1>
        <p className="text-sm text-muted-foreground">
          {empresa?.nombre} · la DFN es lo que se resta del Enterprise Value para llegar al Equity
          Value: lo que deja de cobrar el vendedor. Histórico por las partidas del balance,
          proyectado por el consolidado. <span className="font-semibold">Gris = real</span> ·{" "}
          <span className="text-proy font-semibold">amarillo "Proy." = proyectado</span>.
        </p>
      </div>

      {/* Bloque 1 · DFN contable */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle>1 · DFN contable</CardTitle>
              <CardDescription>
                Deudas LP + CP + otras asimiladas − tesorería − inversiones financieras c/p. Pasa el ratón por cada concepto para ver su procedencia.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <label className="flex items-center gap-2 cursor-pointer" title="Quítalas si no son líquidas de verdad (imposiciones pignoradas, participaciones…)">
                <Switch
                  checked={!!cfg.restar_ifcp}
                  disabled={readOnly}
                  onCheckedChange={(val) => guardarConfig({ restar_ifcp: val }, val ? "Las inversiones financieras c/p restan de la DFN" : "Las inversiones financieras c/p ya no restan")}
                  data-testid="dfn-switch-ifcp"
                />
                Restar inversiones financieras c/p
              </label>
              <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setDialogo(true)} disabled={readOnly} data-testid="dfn-editar-asimiladas">
                <Pencil className="h-3.5 w-3.5" /> Otras deudas asimiladas
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="text-sm">
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="min-w-[260px]">Concepto (€)</TableHead>
                {anios.map((a) => (
                  <TableHead key={a} className={cn("text-right whitespace-nowrap", esProy(a) && "text-proy")}>
                    {a}
                    <span className="block text-[10px] font-normal opacity-70">{esProy(a) ? "Proy." : "Hist."}</span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {FILAS.map((fila, i) =>
                fila.sep ? (
                  <TableRow key={`s-${i}`} className="bg-primary/10">
                    <TableCell colSpan={anios.length + 1} className="font-semibold text-[11px] uppercase tracking-wide text-primary py-1">
                      {fila.sep}
                    </TableCell>
                  </TableRow>
                ) : (
                  <Fragment key={fila.k}>
                    <TableRow className={cn("hover:bg-muted/20", fila.strong && "font-semibold bg-primary/5")} data-testid={`dfn-fila-${fila.k}`}>
                      <TableCell title={fila.hint} className="whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          {fila.editable && (dfn.asimiladas_detalle || []).length > 0 && (
                            <button type="button" className="text-muted-foreground" onClick={() => setAsimAbiertas((s) => !s)} title="Desglose">
                              {asimAbiertas ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          )}
                          {fila.l}
                          {fila.hint && <Info className="h-3 w-3 text-muted-foreground/60" />}
                        </span>
                      </TableCell>
                      {anios.map((a) => (
                        <TableCell key={a} className={cn("text-right font-mono tabular-nums whitespace-nowrap", esProy(a) && "bg-proy text-proy")}>
                          {celda(fila, a)}
                        </TableCell>
                      ))}
                    </TableRow>
                    {fila.editable && asimAbiertas && (dfn.asimiladas_detalle || []).map((aj, j) => (
                      <TableRow key={`asim-${j}`} className="bg-amber-50/40 dark:bg-amber-950/10 text-xs">
                        <TableCell className="pl-8 text-amber-700 dark:text-amber-400 whitespace-nowrap">↳ {aj.concepto}</TableCell>
                        {anios.map((a) => (
                          <TableCell key={a} className="text-right font-mono tabular-nums whitespace-nowrap text-amber-700 dark:text-amber-400">
                            {fmt(aj.por_anio?.[String(a)])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </Fragment>
                )
              )}
            </TableBody>
          </Table>
          {(dfn.supuestos || []).length > 0 && (
            <ul className="px-4 py-3 text-[11px] text-muted-foreground space-y-0.5 border-t">
              {dfn.supuestos.map((s, i) => <li key={i}>· {s}</li>)}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Bloque 2 · Conciliación */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                2 · Conciliación · Balance {base} ↔ Pool bancario declarado
                {conc && (
                  <Badge variant={conc.ok ? "secondary" : "destructive"} data-testid="dfn-conciliacion-check">
                    {conc.ok ? "✓ OK" : "⚠ REVISAR"} · diferencia {fmt(conc.diferencia?.total)}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                La deuda del balance del año base frente a lo declarado en Financiación (préstamos existentes = LP, productos de circulante = CP).
                Si la diferencia es grande, hay deuda sin inventariar en el pool o el pool está a otra fecha: es lo primero que mira una due diligence.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span>Umbral (€)</span>
              <Input type="number" min="0" step="1000" className="h-7 w-28 text-right font-mono text-xs" value={umbral}
                disabled={readOnly} onChange={(e) => setUmbral(e.target.value)} data-testid="dfn-umbral" />
              <Button variant="outline" size="sm" className="h-7 text-xs"
                disabled={readOnly || String(cfg.umbral_conciliacion ?? "") === umbral}
                onClick={guardarUmbral}>
                Guardar
              </Button>
              <Link to={`/empresas/${empresaId}/proyeccion?tab=financiacion`} className="inline-flex items-center gap-1 text-primary hover:underline">
                Ir a Financiación <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {!conc ? (
            <p className="px-4 py-4 text-sm text-muted-foreground">
              No hay pool declarado: da de alta los préstamos existentes y los productos de circulante en Proyección → Financiación → Pool bancario para conciliar.
            </p>
          ) : (
            <>
              <Table className="text-sm">
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="min-w-[260px]">Concepto (€)</TableHead>
                    <TableHead className="text-right">Balance {base}</TableHead>
                    <TableHead className="text-right">Pool declarado</TableHead>
                    <TableHead className="text-right">Diferencia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[["Deudas a largo plazo", "lp"], ["Deudas a corto plazo", "cp"], ["TOTAL DEUDA FINANCIERA", "total"]].map(([l, k]) => (
                    <TableRow key={k} className={cn(k === "total" && "font-semibold bg-primary/5")}>
                      <TableCell>{l}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmt(conc.balance?.[k])}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmt(conc.pool?.[k])}</TableCell>
                      <TableCell className={cn("text-right font-mono tabular-nums", Math.abs(conc.diferencia?.[k] || 0) >= conc.umbral ? "text-rose-600" : "")}>{fmt(conc.diferencia?.[k])}</TableCell>
                    </TableRow>
                  ))}
                  {conc.deuda_no_cubierta != null && (
                    <TableRow className="text-xs text-amber-700 dark:text-amber-400">
                      <TableCell colSpan={3} title="resumen_financiacion.deuda_resto del consolidado">
                        Deuda del balance no cubierta por el pool declarado — el modelo la proyecta PLANA mientras no se declare (socios, CDTI/FILPE/ICF, vencimientos corrientes…)
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums" data-testid="dfn-deuda-no-cubierta">{fmt(conc.deuda_no_cubierta)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="px-4 py-3 border-t text-xs text-muted-foreground">
                <p className="font-medium mb-1">Pool declarado ({conc.pool?.detalle?.length || 0} operaciones)</p>
                <ul className="grid gap-x-6 gap-y-0.5 sm:grid-cols-2">
                  {(conc.pool?.detalle || []).map((d, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span>{d.tipo === "poliza" ? "Circulante" : "Préstamo"} · {d.nombre}{d.vencimiento ? ` (vto. ${d.vencimiento})` : ""}</span>
                      <span className="font-mono">{fmt(d.importe)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Bloque 3 · Composición por cuenta */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>3 · Composición de la deuda financiera {base} por cuenta</CardTitle>
          <CardDescription>
            Las cuentas de las partidas de deuda del año base, agrupadas a 3 dígitos (170 bancaria · 171 otras y socios · 520 vencimiento corriente · 5201 pólizas · 5208 descuento · 521 no bancaria…). Sale sola del Sumas y Saldos. Importe positivo = deuda.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="text-sm">
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="min-w-[260px]">Partida · cuenta</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead className="text-right">Saldo {base}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(dfn.composicion || []).length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-4">Sin deuda financiera en el balance de {base}</TableCell></TableRow>
              )}
              {(dfn.composicion || []).map((g, i) => {
                const key = `${g.partida}-${g.cuenta3}`;
                const abierto = !!gruposAbiertos[key];
                return (
                  <Fragment key={key}>
                    <TableRow className="hover:bg-muted/20 cursor-pointer" onClick={() => setGruposAbiertos((s) => ({ ...s, [key]: !abierto }))} data-testid={`dfn-comp-${g.cuenta3}`}>
                      <TableCell className="whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          {abierto ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                          <span className="text-xs text-muted-foreground">{g.partida.split(".")[0]}.</span> <span className="font-mono">{g.cuenta3}</span>
                          <span className="text-[10px] text-muted-foreground">Σ {g.cuentas.length}</span>
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">{g.partida}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums font-semibold">{fmt(g.saldo)}</TableCell>
                    </TableRow>
                    {abierto && g.cuentas.map((c) => (
                      <TableRow key={`${key}-${c.codigo}`} className="text-xs bg-muted/10">
                        <TableCell className="pl-10 font-mono">{c.codigo}</TableCell>
                        <TableCell>{c.nombre}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{fmt(c.saldo)}</TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Bloque 4 · Del EV al Equity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>4 · Del Enterprise Value al Equity Value: lo que este cuadro no cuantifica</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-1">
          <p>· La DFN se presenta con su signo técnico: negativa = caja neta a favor del vendedor.</p>
          <p>· Los préstamos de socios y la deuda con el grupo cuentan como DFN, pero se negocian aparte: normalmente se cancelan o se compensan contra el precio en el cierre.</p>
          <p>· La deuda no bancaria (CDTI, FILPE, ICF, ENISA, Tesoro Público) es DFN a efectos de transacción aunque no esté en el pool bancario.</p>
          <p>· Los rentings y arrendamientos operativos no son deuda en PGC Pymes; si el comprador los trata como tal (criterio IFRS 16), decláralos en «otras deudas asimiladas».</p>
          <p>· Caja mínima operativa, cajas pignoradas o atrapadas, provisiones por litigios y garantías se discuten en el SPA y no están aquí.</p>
          <p>· Para comparar con cifras intermedias (p.ej. seis meses reales) el EBITDA debe anualizarse; las columnas proyectadas usan EBITDA de ejercicio completo.</p>
        </CardContent>
      </Card>

      <AsimiladasDialog
        open={dialogo}
        onOpenChange={setDialogo}
        anios={anios}
        esProy={esProy}
        inicial={cfg.asimiladas || []}
        onGuardar={(limpias) => guardarConfig({ asimiladas: limpias }, "Otras deudas asimiladas guardadas")}
      />
    </div>
  );
}
